import { of, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';

import { ApmTranslatePipe } from '@mitsubishi/core';
import { StudyService } from '@mitsubishi/project/data-access';
import { FormlyFormWrapperComponent } from '@mitsubishi/shared/components';
import { StudySubcategory } from '@mitsubishi/shared/data-access';
import { ButtonComponent } from '@mitsubishi/shared/ui';
import { ApmSidebarModule, ApmSidebarService, SIDEBAR_DATA } from '@mitsubishi/ui/sidebar';
import { WindowedSpinnerComponent } from '@mitsubishi/ui/spinner';

import { FormlyFieldConfig } from '@ngx-formly/core';

export type StudyReadonlyWorkingFilterConditionsSidebarComponentData = {
  projectId: string;
  studyId: string;
  workingConditionId: string;
  studySubcategory: StudySubcategory;
};

@Component({
  selector: 'apm-readonly-working-filter-conditions-sidebar',
  imports: [ApmSidebarModule, ApmTranslatePipe, ButtonComponent, FormlyFormWrapperComponent, WindowedSpinnerComponent],
  templateUrl: './study-readonly-working-filter-conditions-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudyReadonlyWorkingFilterConditionsSidebarComponent {
  #sidebarService = inject(ApmSidebarService);
  #studyService = inject(StudyService);

  sidebarData = inject(SIDEBAR_DATA) as StudyReadonlyWorkingFilterConditionsSidebarComponentData;
  readonly isLoadingWorkingConditions = signal(true);
  readonly isLoadingFilters = signal(true);

  readonly isLoading = computed(() => {
    return this.isLoadingWorkingConditions() || this.isLoadingFilters();
  });

  wcForm = new FormGroup({});
  wcFormModel: Record<string, unknown> = {};
  readonly wcFields = toSignal(
    this.#studyService
      .getWorkingConditions(
        this.sidebarData.projectId,
        this.sidebarData.studyId,
        this.sidebarData.studySubcategory.subcategoryId,
        this.sidebarData.studySubcategory.productTypeId,
        this.sidebarData.workingConditionId,
        this.sidebarData.studySubcategory.reversalCycleId
      )
      .pipe(
        tap(() => {
          this.isLoadingWorkingConditions.set(false);
          setTimeout(() => {
            this.wcForm.disable();
          }, 50);
        }),
        catchError(() => {
          this.isLoadingWorkingConditions.set(false);

          return of([]);
        })
      ),
    { initialValue: [] as FormlyFieldConfig[] }
  );

  filtersForm = new FormGroup({});
  filtersFormModel: Record<string, unknown> = {};
  readonly filtersFields = toSignal(
    this.#studyService.getFilterConditions(this.sidebarData.projectId, this.sidebarData.studySubcategory.uuid).pipe(
      tap(() => {
        this.isLoadingFilters.set(false);
        setTimeout(() => {
          this.filtersForm.disable();
        }, 50);
      }),
      catchError(() => {
        this.isLoadingFilters.set(false);

        return of([]);
      })
    ),
    { initialValue: [] as FormlyFieldConfig[] }
  );

  close() {
    this.#sidebarService.closeSidebar();
  }
}
